import React, { useEffect } from 'react';
import { BrowserRouter as Router, Routes, Route, Navigate, useLocation } from 'react-router-dom';
import { ToastContainer } from 'react-toastify';
import { Toaster } from 'react-hot-toast';
import 'react-toastify/dist/ReactToastify.css';
import Login from './components/auth/Login';
import SuperAdminLogin from './components/auth/SuperAdminLogin';
import ForgotPassword from './components/auth/ForgotPassword';
import { ForgotPasswordProvider } from './context/ForgotPasswordContext';
import ResetPassword from './components/auth/ResetPassword';
import { ResetPasswordProvider } from './context/ResetPasswordContext';
import Dashboard from './components/dashboard/Dashboard';
import { AuthProvider, useAuth } from './context/AuthContext';
import { LoadingProvider } from './context/LoadingContext';
import GuestRoute from './components/auth/GuestRoute';
import AuthRoute from './components/auth/AuthRoute';
import { loadAllScripts } from './utils/externalScripts';
import AdminDashboard from './components/admin/AdminDashboard';
import useRouteChange from './hooks/useRouteChange';
import ErrorBoundary from './components/common/ErrorBoundary';
import CustomerForm from './components/customers/CustomerForm';
import CustomerList from './components/customers/CustomerList';
import CustomerView from './components/customers/CustomerView';   
import DashboardLayout from './components/layouts/DashboardLayout';
import { CustomerProvider } from './context/CustomerContext';
import { JobProvider } from './context/JobContext';
import JobList from './components/jobs/JobList';
import JobForm from './components/jobs/JobForm';
import JobView from './components/jobs/JobView';
import EstimationList from './components/estimations/EstimationList';
import EstimationForm from './components/estimations/EstimationForm';
import EstimationView from './components/estimations/EstimationView';
import AcceptEstimation from './components/estimations/AcceptEstimation';
import { EstimationProvider } from './context/EstimationContext';
import EstimationTemplateList from './components/estimation-templates/EstimationTemplateList';
import EstimationTemplateForm from './components/estimation-templates/EstimationTemplateForm';
import EstimationTemplateView from './components/estimation-templates/EstimationTemplateView';
import { EstimationTemplateProvider } from './context/EstimationTemplateContext';
import Profile from './components/profile/Profile';
import ChangePassword from './components/auth/ChangePassword';
import { ChangePasswordProvider } from './context/ChangePasswordContext';
import ProjectsList from './components/projects/ProjectsList';
import ProjectForm from './components/projects/ProjectForm';
import ProjectView from './components/projects/ProjectView';
import { ProjectProvider } from './context/ProjectContext';
import ServiceCategoriesList from './components/service-categories/ServiceCategoriesList';
import ServiceCategoryForm from './components/service-categories/ServiceCategoryForm';
import ServiceCategoryView from './components/service-categories/ServiceCategoryView';
import { ServiceCategoryProvider } from './context/ServiceCategoryContext';
import { ServiceProvider } from './context/ServiceContext';
import ServiceList from './components/services/ServiceList';
import ServiceForm from './components/services/ServiceForm';
import ServiceView from './components/services/ServiceView';
import { ProductCategoryProvider } from './context/ProductCategoryContext';
import ProductCategoryList from './components/product-categories/ProductCategoryList';
import ProductCategoryForm from './components/product-categories/ProductCategoryForm';
import ProductCategoryView from './components/product-categories/ProductCategoryView';
import { EquipmentManufacturerProvider } from './context/EquipmentManufacturerContext';
import EquipmentManufacturerList from './components/equipment-manufacturers/EquipmentManufacturerList';
import EquipmentManufacturerForm from './components/equipment-manufacturers/EquipmentManufacturerForm';
import EquipmentManufacturerView from './components/equipment-manufacturers/EquipmentManufacturerView';
import { EquipmentTypeProvider } from './context/EquipmentTypeContext';
import EquipmentTypeList from './components/equipment-types/EquipmentTypeList';
import EquipmentTypeForm from './components/equipment-types/EquipmentTypeForm';
import EquipmentTypeView from './components/equipment-types/EquipmentTypeView';
import { ProductProvider } from './context/ProductContext';
import ProductList from './components/products/ProductList';
import ProductForm from './components/products/ProductForm';
import ProductView from './components/products/ProductView';
import VendorList from './components/vendors/VendorList'; 
import VendorForm from './components/vendors/VendorForm'; 
import VendorView from './components/vendors/VendorView';
import { VendorProvider } from './context/VendorContext';
import InvoiceList from './components/invoices/InvoiceList'; 
import InvoiceForm from './components/invoices/InvoiceForm'; 
import InvoiceView from './components/invoices/InvoiceView';
import { InvoiceProvider } from './context/InvoiceContext';
import PaymentList from './components/payments/PaymentList';
import PaymentForm from './components/payments/PaymentForm';
import PaymentView from './components/payments/PaymentView';
import { PaymentProvider } from './context/PaymentContext';
import ExpenseCategoryList from './components/expenses-categories/ExpenseCategoryList'; 
import ExpenseCategoryForm from './components/expenses-categories/ExpenseCategoryForm';
import ExpenseCategoryView from './components/expenses-categories/ExpenseCategoryView';
import { ExpenseCategoryProvider } from './context/ExpenseCategoryContext';
import JobCategoryList from './components/job-categories/JobCategoryList';
import JobCategoryView from './components/job-categories/JobCategoryView';
import JobCategoryForm from './components/job-categories/JobCategoryForm';
import { JobCategoryProvider } from './context/JobCategoryContext';
import TimeOffRequestTypeList from './components/time-off-request-types/TimeOffRequestTypeList';
import TimeOffRequestTypeForm from './components/time-off-request-types/TimeOffRequestTypeForm';
import TimeOffRequestTypeView from './components/time-off-request-types/TimeOffRequestTypeView';
import TimeOffRequestView from './components/time-off-requests/TimeOffRequestView';
import { TimeOffRequestTypeProvider } from './context/TimeOffRequestTypeContext';
import TimeOffRequestsList from './components/time-off-requests/TimeOffRequestsList';
import TimeOffRequestsForm from './components/time-off-requests/TimeOffRequestsForm';
import { TimeOffRequestsProvider } from './context/TimeOffRequestsContext';
import { PaymentMethodProvider } from './context/PaymentMethodContext';
import PaymentMethodList from './components/payment-methods/PaymentMethodList';
import PaymentMethodForm from './components/payment-methods/PaymentMethodForm';
import PaymentMethodView from './components/payment-methods/PaymentMethodView';
import PaymentTermList from './components/payment-terms/PaymentTermList';
import PaymentTermForm from './components/payment-terms/PaymentTermForm';
import PaymentTermView from './components/payment-terms/PaymentTermView';
import { PaymentTermProvider } from './context/PaymentTermContext';
import CustomerTagList from './components/customer-tags/CustomerTagList';
import CustomerTagForm from './components/customer-tags/CustomerTagForm';
import CustomerTagView from './components/customer-tags/CustomerTagView';
import { CustomerTagProvider } from './context/CustomerTagContext';
import EstimateJobTagList from './components/estimate-job-tags/EstimateJobTagList';
import { EstimateJobTagProvider } from './context/EstimateJobTagContext';
import EstimateJobTagForm from './components/estimate-job-tags/EstimateJobTagForm';
import EstimateJobTagView from './components/estimate-job-tags/EstimateJobTagView';
import CompanyInfo from './components/company-info/CompanyInfo';
import { CompanyInfoProvider } from './context/CompanyInfoContext';
import CompanyInfoForm from './components/company-info/CompanyInfoForm';
import CompanyInfoView from './components/company-info/CompanyInfoView';
import { CommunicationTemplatesProvider } from './context/CommunicationTemplatesContext';
import CommunicationTemplates from './components/communication-templates/CommunicationTemplates';
import CommunicationTemplateForm from './components/communication-templates/CommunicationTemplateForm';
import { CommunicationTemplateCategoryProvider } from './context/CommunicationTemplateCategoryContext';
import CompanyWorkForce from './components/company-workforce/CompanyWorkForce';
import CompanyWorkForceForm from './components/company-workforce/CompanyWorkForceForm';
import CompanyWorkForceView from './components/company-workforce/CompanyWorkForceView';
import { CompanyWorkForceProvider } from './context/CompanyWorkForceContext';
import { StatusCategoriesProvider } from './context/StatusCategoriesContext';
import { StatusProvider } from './context/StatusContext';
import EstimationStatusList from './components/estimation-statuses/EstimationStatusList';
import EstimationStatusForm from './components/estimation-statuses/EstimationStatusForm';
import JobStatusList from './components/job-statuses/JobStatusList';
import JobStatusForm from './components/job-statuses/JobStatusForm';
import MyCalendar from './components/calendar/Calendar';
import TaskForm from './components/tasks/TaskForm';
import TaskList from './components/tasks/TaskList'
import TaskView from './components/tasks/TaskView'
import { TaskProvider } from './context/TaskContext';
import ReportDashboard from './components/reports/ReportDashboard'
import { ErrorProvider } from './context/ErrorContext';
import useAutoLogoutOnIdle from './hooks/useAutoLogoutOnIdle';
import NotFound from './components/common/NotFound';
import { CountryProvider, useCountry } from './context/CountryContext';
import { RoleProvider } from './context/RoleContext';
import RoleList from './components/role/RoleList';
import RoleForm from './components/role/RoleForm';
import $ from 'jquery';
import 'jquery-ui/ui/widgets/datepicker';
import 'jquery-ui/themes/base/all.css'; // Optional but adds default styling
import { useForm } from 'react-hook-form';
import { PaymentTypeProvider } from './context/PaymentTypeContext';
import "primereact/resources/themes/lara-light-blue/theme.css"; 
import "primereact/resources/primereact.min.css";
import "primeicons/primeicons.css";
import { PermissionsProvider,usePermissions } from './context/PermissionsContext';
 
const adminPrefix = process.env.REACT_APP_ADMIN_ROUTE_PREFIX || 'admin';
// Protected Route component with role-based access control
 
const ProtectedRoute = ({ children, adminOnly = false, hasPermission = true}) => {
 
  const { user } = useAuth();
  const location = useLocation();
  
  if (!user) {
   
    return <Navigate to="/login" state={{ from: location }} replace />;
  }
 
  if (!hasPermission) {
    return <Navigate to="/404" replace />;
  }

  // For admin routes, check if user is admin
  if (adminOnly && user.role_id !== 1) {
    
    return <Navigate to="/dashboard" replace />;
  }
  
  // For non-admin routes, if user is admin and trying to access regular routes,
  // redirect them to the admin version of the route
  if (!adminOnly && user.role_id === 1 && !location.pathname.startsWith('/'+adminPrefix)) {
    const adminPath = `${adminPrefix}${location.pathname}`;
    
    return <Navigate to={adminPath} replace />;
  }

  // For non-admin users trying to access admin routes
  if (user.role_id !== 1 && location.pathname.startsWith('/'+adminPrefix)) {
    
    return <Navigate to="/dashboard" replace />;
  }

  return children;
};

// AppRoutes component to use the route change hook
const AppRoutes = () => {
  const { user } = useAuth();
 
  useRouteChange();
  useAutoLogoutOnIdle();
  const { permission } = usePermissions();
  
  if (!user) {
   
    return (
      <Routes  element={<ProtectedRoute ><DashboardLayout /></ProtectedRoute>}>
        <Route path="/" element={<Navigate to="/login" replace />} />
        <Route path="/login" element={<GuestRoute><Login /></GuestRoute>} />
        <Route path="/forgot-password" element={<GuestRoute><ForgotPassword /></GuestRoute>} />
        <Route path="/reset-password" element={<GuestRoute><ResetPassword /></GuestRoute>} />
        <Route path={`${adminPrefix}/login`} element={<GuestRoute><SuperAdminLogin /></GuestRoute>} />
        <Route path={`${adminPrefix}`} element={<GuestRoute><SuperAdminLogin /></GuestRoute>} />
        <Route path="*"  element={<Navigate to="/login" replace />}/>
        <Route path="/dashboard" element={<AuthRoute><Dashboard /></AuthRoute>} />
        <Route path="/estimations/accept/:id" element={<AcceptEstimation />} />
      </Routes> 
    );
  }

  return (
    <Routes>
      {/* Regular user routes */}
       
      <Route element={<ProtectedRoute ><DashboardLayout /></ProtectedRoute>}>
        <Route path="*" element={ <NotFound />  } />
        <Route path="/login" element={<GuestRoute><Login /></GuestRoute>} />
        <Route path="/" element={<Navigate to="/dashboard" replace />} />
        <Route path="/dashboard" element={<ProtectedRoute hasPermission={permission(1, 'view')}><Dashboard /></ProtectedRoute>} />
        <Route path="/customers" element={<ProtectedRoute hasPermission={permission(28, 'view')}><CustomerList /></ProtectedRoute>} />
        <Route path="/customers/view/:id" element={<ProtectedRoute hasPermission={permission(28, 'view')}><CustomerView /></ProtectedRoute>} />
        <Route path="/customers/new" element={<ProtectedRoute hasPermission={permission(28, 'create')}><CustomerForm /></ProtectedRoute>} />
        <Route path="/customers/edit/:id" element={<ProtectedRoute hasPermission={permission(28, 'update')}><CustomerForm /></ProtectedRoute>} />
        <Route path="/jobs" element={<ProtectedRoute hasPermission={permission(39, 'view')}><JobList /></ProtectedRoute>} />
        <Route path="/jobs/new" element={<ProtectedRoute hasPermission={permission(39, 'create')}><JobForm /></ProtectedRoute>} />
        <Route path="/jobs/edit/:id" element={<ProtectedRoute hasPermission={permission(39, 'update')}><JobForm /></ProtectedRoute>} />
        <Route path="/jobs/view/:id" element={<ProtectedRoute hasPermission={permission(39, 'view')}><JobView /></ProtectedRoute>} />
        <Route path="/estimations"  element={<ProtectedRoute hasPermission={permission(40, 'view')}><EstimationList /></ProtectedRoute>} />
        <Route path="/estimations/new" element={<ProtectedRoute hasPermission={permission(40, 'create')}><EstimationForm /></ProtectedRoute>} />
        <Route path="/estimations/edit/:id" element={<ProtectedRoute hasPermission={permission(40, 'update')}><EstimationForm /></ProtectedRoute>} />
        <Route path="/estimations/view/:id" element={<ProtectedRoute hasPermission={permission(40, 'view')}><EstimationView /></ProtectedRoute>} />
        <Route path="/estimations/accept/:id" element={<AcceptEstimation />} />
        <Route path="/estimation-templates" element={<EstimationTemplateList />} />
        <Route path="/estimation-templates/new" element={<EstimationTemplateForm />} />
        <Route path="/estimation-templates/edit/:id" element={<EstimationTemplateForm />} />
        <Route path="/estimation-templates/view/:id" element={<EstimationTemplateView />} />
        <Route path="/profile" element={<Profile />} />
        <Route path="/my-office" element={<div>My Office Page</div>} />
        <Route path="/projects" element={<ProtectedRoute hasPermission={permission(29, 'view')}><ProjectsList /></ProtectedRoute>} />
        <Route path="/projects/create" element={<ProtectedRoute hasPermission={permission(29, 'create')}><ProjectForm /></ProtectedRoute>} />
        <Route path="/projects/edit/:id" element={<ProtectedRoute hasPermission={permission(29, 'update')}><ProjectForm /></ProtectedRoute>} />
        <Route path="/projects/view/:id" element={<ProtectedRoute hasPermission={permission(29, 'view')}><ProjectView /></ProtectedRoute>} />
        <Route path="/estimates" element={<div>Estimates Page</div>} />
        <Route path="/dispatch" element={<div>Dispatch Page</div>} />
        <Route path="/accounting" element={<div>Accounting Page</div>} />
        <Route path="/change-password" element={<ChangePassword />} />
        <Route path="/service-categories" element={<ProtectedRoute hasPermission={permission(23, 'view')}><ServiceCategoriesList /></ProtectedRoute>} />
        <Route path="/service-categories/create" element={<ProtectedRoute hasPermission={permission(23, 'create')}><ServiceCategoryForm /></ProtectedRoute>} />
        <Route path="/service-categories/edit/:id" element={<ProtectedRoute hasPermission={permission(23, 'update')}><ServiceCategoryForm /></ProtectedRoute>} />
        <Route path="/service-categories/view/:id" element={<ProtectedRoute hasPermission={permission(23, 'view')}><ServiceCategoryView /></ProtectedRoute>} />
        <Route path="/services" element={<ProtectedRoute hasPermission={permission(23, 'view')}><ServiceList /></ProtectedRoute>} />
        <Route path="/services/new" element={<ProtectedRoute hasPermission={permission(23, 'create')}><ServiceForm /></ProtectedRoute>} />
        <Route path="/services/edit/:id" element={<ProtectedRoute hasPermission={permission(23, 'update')}><ServiceForm /></ProtectedRoute>} />
        <Route path="/services/view/:id" element={<ProtectedRoute hasPermission={permission(23, 'view')}><ServiceView /></ProtectedRoute>} />
        <Route path="/product-categories" element={<ProtectedRoute hasPermission={permission(21, 'view')}><ProductCategoryList /></ProtectedRoute>} />
        <Route path="/product-categories/create" element={<ProtectedRoute hasPermission={permission(21, 'create')}><ProductCategoryForm /></ProtectedRoute>} />
        <Route path="/product-categories/edit/:id" element={<ProtectedRoute hasPermission={permission(21, 'update')}><ProductCategoryForm /></ProtectedRoute>} />
        <Route path="/product-categories/view/:id" element={<ProtectedRoute hasPermission={permission(21, 'view')}><ProductCategoryView /></ProtectedRoute>} />
        <Route path="/equipment-manufacturers" element={<EquipmentManufacturerList />} />
        <Route path="/equipment-manufacturers/create" element={<EquipmentManufacturerForm />} />
        <Route path="/equipment-manufacturers/edit/:id" element={<EquipmentManufacturerForm />} />
        <Route path="/equipment-manufacturers/view/:id" element={<EquipmentManufacturerView />} />
        <Route path="/equipment-types" element={<EquipmentTypeList />} />
        <Route path="/equipment-types/create" element={<EquipmentTypeForm />} />
        <Route path="/equipment-types/edit/:id" element={<EquipmentTypeForm />} />
        <Route path="/equipment-types/view/:id" element={<EquipmentTypeView />} />
        <Route path="/products" element={<ProtectedRoute hasPermission={permission(21, 'view')}><ProductList /></ProtectedRoute>} />
        <Route path="/products/create" element={<ProtectedRoute hasPermission={permission(21, 'create')}><ProductForm /></ProtectedRoute>} />
        <Route path="/products/edit/:id" element={<ProtectedRoute hasPermission={permission(21, 'update')}><ProductForm /></ProtectedRoute>} />
        <Route path="/products/view/:id" element={<ProtectedRoute hasPermission={permission(21, 'view')}><ProductView /></ProtectedRoute>} />
        <Route path="/vendors" element={<VendorList />} />
        <Route path="/vendors/create" element={<VendorForm />} />
        <Route path="/vendors/edit/:id" element={<VendorForm />} />
        <Route path="/vendors/view/:id" element={<VendorView />} />
        <Route path="/invoices" element={<ProtectedRoute hasPermission={permission(34, 'view')}><InvoiceList /></ProtectedRoute>} />
        <Route path="/invoices/create" element={<ProtectedRoute hasPermission={permission(34, 'create')}><InvoiceForm /></ProtectedRoute>} />
        <Route path="/invoices/edit/:id" element={<ProtectedRoute hasPermission={permission(34, 'update')}><InvoiceForm /></ProtectedRoute>} />
        <Route path="invoices/view/:id" element={<ProtectedRoute hasPermission={permission(34, 'view')}><InvoiceView /></ProtectedRoute>} />
        <Route path="/payments" element={<ProtectedRoute hasPermission={permission(35, 'view')}><PaymentList /></ProtectedRoute>} />
        <Route path="/payments/create" element={<ProtectedRoute hasPermission={permission(35, 'create')}><PaymentForm /></ProtectedRoute>} />
        <Route path="/payments/edit/:id" element={<ProtectedRoute hasPermission={permission(35, 'update')}><PaymentForm /></ProtectedRoute>} />
        <Route path="/payments/view/:id" element={<ProtectedRoute hasPermission={permission(35, 'view')}><PaymentView /></ProtectedRoute>} />
        <Route path="/expense-categories" element={<ExpenseCategoryList />} />
        <Route path="/expense-categories/create" element={<ExpenseCategoryForm />} />
        <Route path="/expense-categories/edit/:id" element={<ExpenseCategoryForm />} />
        <Route path="/expense-categories/view/:id" element={<ExpenseCategoryView />} />
        <Route path="/job-categories" element={<JobCategoryList />} />
        <Route path="/job-categories/create" element={<JobCategoryForm />} />
        <Route path="/job-categories/edit/:id" element={<JobCategoryForm />} />
        <Route path="/job-categories/view/:id" element={<JobCategoryView />} />
        <Route path="/time-off-request-types" element={<TimeOffRequestTypeList />} />
        <Route path="/time-off-request-types/create" element={<TimeOffRequestTypeForm />} />
        <Route path="/time-off-request-types/edit/:id" element={<TimeOffRequestTypeForm />} />
        <Route path="/time-off-request-types/view/:id" element={<TimeOffRequestTypeView />} />
        <Route path="/time-off-requests" element={<TimeOffRequestsList />} />
        <Route path="/time-off-requests/create" element={<TimeOffRequestsForm />} />
        <Route path="/time-off-requests/edit/:id" element={<TimeOffRequestsForm />} />
        <Route path="/time-off-requests/view/:id" element={<TimeOffRequestView />} />
        <Route path="/payment-methods" element={<PaymentMethodList />} />
        <Route path="/payment-methods/create" element={<PaymentMethodForm />} />
        <Route path="/payment-methods/edit/:id" element={<PaymentMethodForm />} />
        <Route path="/payment-methods/view/:id" element={<PaymentMethodView />} />
        <Route path="/payment-terms" element={<PaymentTermList />} />
        <Route path="/payment-terms/create" element={<PaymentTermForm />} />
        <Route path="/payment-terms/edit/:id" element={<PaymentTermForm />} />
        <Route path="/payment-terms/view/:id" element={<PaymentTermView />} />
        <Route path="/customer-tags" element={<CustomerTagList />} />
        <Route path="/customer-tags/create" element={<CustomerTagForm />} />
        <Route path="/customer-tags/edit/:id" element={<CustomerTagForm />} />
        <Route path="/customer-tags/view/:id" element={<CustomerTagView />} />
        <Route path="/estimate-job-tags" element={<EstimateJobTagList />} />
        <Route path="/estimate-job-tags/create" element={<EstimateJobTagForm />} />
        <Route path="/estimate-job-tags/edit/:id" element={<EstimateJobTagForm />} />
        <Route path="/estimate-job-tags/view/:id" element={<EstimateJobTagView />} />
        <Route path="/company-info" element={<ProtectedRoute hasPermission={permission(4, 'view')}><CompanyInfo /></ProtectedRoute>} />
        <Route path="/company-info/create" element={<ProtectedRoute hasPermission={permission(4, 'create')}><CompanyInfoForm /></ProtectedRoute>} />
        <Route path="/company-info/edit/:id" element={<ProtectedRoute hasPermission={permission(4, 'update')}><CompanyInfoForm /></ProtectedRoute>} />
        <Route path="/company-info/view/:id" element={<ProtectedRoute hasPermission={permission(4, 'view')}><CompanyInfoView /></ProtectedRoute>} />
        <Route path="/communication-templates" element={<CommunicationTemplates />} />
        <Route path="/communication-templates/create" element={<CommunicationTemplateForm />} />
        <Route path="/communication-templates/edit/:id" element={<CommunicationTemplateForm />} />
        <Route path="/company-workforces" element={<ProtectedRoute hasPermission={permission(14, 'view')}><CompanyWorkForce /></ProtectedRoute>} />
        <Route path="/company-workforces/create" element={<ProtectedRoute hasPermission={permission(14, 'create')}><CompanyWorkForceForm /></ProtectedRoute>} />
        <Route path="/company-workforces/edit/:id" element={<ProtectedRoute hasPermission={permission(14, 'update')}><CompanyWorkForceForm /></ProtectedRoute>} />
        <Route path="/company-workforces/view/:id" element={<ProtectedRoute hasPermission={permission(14, 'view')}><CompanyWorkForceView /></ProtectedRoute>} />
        <Route path="/calendar" element={<ProtectedRoute hasPermission={permission(33, 'view')}><MyCalendar /></ProtectedRoute>} /> 
        <Route path="/tasks" element={<TaskList />} />
        <Route path="/tasks/new" element={<TaskForm />} /> 
        <Route path="/tasks/edit/:id" element={<TaskForm />} /> 
        <Route path="/tasks/view/:id" element={<TaskView />} />
        <Route path="/reports" element={<ReportDashboard />} />
        <Route path="/estimation-statuses" element={<ProtectedRoute hasPermission={permission(7, 'view')}><EstimationStatusList /></ProtectedRoute>} />
        <Route path="/estimation-statuses/new" element={<ProtectedRoute hasPermission={permission(7, 'create')}><EstimationStatusForm /></ProtectedRoute>} />
        <Route path="/estimation-statuses/edit/:id" element={<ProtectedRoute hasPermission={permission(7, 'update')}><EstimationStatusForm /></ProtectedRoute>} />
        <Route path="/job-statuses" element={<ProtectedRoute hasPermission={permission(7, 'view')}><JobStatusList /></ProtectedRoute>} />
        <Route path="/job-statuses/new" element={<ProtectedRoute hasPermission={permission(7, 'create')}><JobStatusForm /></ProtectedRoute>} />
        <Route path="/job-statuses/edit/:id" element={<ProtectedRoute hasPermission={permission(7, 'update')}><JobStatusForm /></ProtectedRoute>} />

      </Route>

      {/* Admin routes */}
      <Route path={`${adminPrefix}/`} element={<ProtectedRoute adminOnly><DashboardLayout /></ProtectedRoute>}>
       
        <Route path="login" element={<GuestRoute><SuperAdminLogin /></GuestRoute>} />
        <Route index element={<Navigate to="dashboard" replace />} />
        <Route path="dashboard" element={<AdminDashboard />} />
        <Route path="customers" element={<CustomerList />} />
        <Route path="customers/new" element={<CustomerForm />} />
        <Route path="customers/edit/:id" element={<CustomerForm />} />
        <Route path="customers/view/:id" element={<CustomerView />} />
        <Route path="jobs" element={<JobList />} />
        <Route path="jobs/new" element={<JobForm />} />
        <Route path="jobs/edit/:id" element={<JobForm />} />
        <Route path="jobs/view/:id" element={<JobView />} />
        <Route path="estimations" element={<EstimationList />} />
        <Route path="estimations/new" element={<EstimationForm />} />
        <Route path="estimations/edit/:id" element={<EstimationForm />} />
        <Route path="estimations/view/:id" element={<EstimationView />} />
        <Route path="estimation-templates" element={<EstimationTemplateList />} />
        <Route path="estimation-templates/new" element={<EstimationTemplateForm />} />
        <Route path="estimation-templates/edit/:id" element={<EstimationTemplateForm />} />
        <Route path="estimation-templates/view/:id" element={<EstimationTemplateView />} />
        <Route path="profile" element={<Profile />} />
        <Route path="change-password" element={<ChangePassword />} />
        <Route path="projects" element={<ProjectsList />} />
        <Route path="projects/create" element={<ProjectForm />} />
        <Route path="projects/edit/:id" element={<ProjectForm />} />
        <Route path="projects/view/:id" element={<ProjectView />} />
        <Route path="service-categories" element={<ServiceCategoriesList />} />
        <Route path="service-categories/create" element={<ServiceCategoryForm />} />
        <Route path="service-categories/edit/:id" element={<ServiceCategoryForm />} />
        <Route path="service-categories/view/:id" element={<ServiceCategoryView />} />
        <Route path="services" element={<ServiceList />} />
        <Route path="services/new" element={<ServiceForm />} />
        <Route path="services/edit/:id" element={<ServiceForm />} />
        <Route path="services/view/:id" element={<ServiceView />} />
        <Route path="product-categories" element={<ProductCategoryList />} />
        <Route path="product-categories/create" element={<ProductCategoryForm />} />
        <Route path="product-categories/edit/:id" element={<ProductCategoryForm />} />
        <Route path="product-categories/view/:id" element={<ProductCategoryView />} />
        <Route path="equipment-manufacturers" element={<EquipmentManufacturerList />} />
        <Route path="equipment-manufacturers/create" element={<EquipmentManufacturerForm />} />
        <Route path="equipment-manufacturers/edit/:id" element={<EquipmentManufacturerForm />} />
        <Route path="equipment-manufacturers/view/:id" element={<EquipmentManufacturerView />} />
        <Route path="equipment-types" element={<EquipmentTypeList />} />
        <Route path="equipment-types/create" element={<EquipmentTypeForm />} />
        <Route path="equipment-types/edit/:id" element={<EquipmentTypeForm />} />
        <Route path="equipment-types/view/:id" element={<EquipmentTypeView />} />
        <Route path="products" element={<ProductList />} />
        <Route path="products/create" element={<ProductForm />} />
        <Route path="products/edit/:id" element={<ProductForm />} />
        <Route path="products/view/:id" element={<ProductView />} />
        <Route path="vendors" element={<VendorList />} />
        <Route path="vendors/create" element={<VendorForm />} />
        <Route path="vendors/edit/:id" element={<VendorForm />} />
        <Route path="vendors/view/:id" element={<VendorView />} />
        <Route path="invoices" element={<InvoiceList />} />
        <Route path="invoices/create" element={<InvoiceForm />} />
        <Route path="invoices/edit/:id" element={<InvoiceForm />} />
        <Route path="invoices/view/:id" element={<InvoiceView />} />
        <Route path="payments" element={<PaymentList />} />
        <Route path="payments/create" element={<PaymentForm />} />
        <Route path="payments/edit/:id" element={<PaymentForm />} />
        <Route path="payments/view/:id" element={<PaymentView />} />
        <Route path="expense-categories" element={<ExpenseCategoryList />} />
        <Route path="expense-categories/create" element={<ExpenseCategoryForm />} />
        <Route path="expense-categories/edit/:id" element={<ExpenseCategoryForm />} />
        <Route path="expense-categories/view/:id" element={<ExpenseCategoryView />} />
        <Route path="job-categories" element={<JobCategoryList />} />
        <Route path="job-categories/create" element={<JobCategoryForm />} />
        <Route path="job-categories/edit/:id" element={<JobCategoryForm />} />
        <Route path="job-categories/view/:id" element={<JobCategoryView />} />
        <Route path="time-off-request-types" element={<TimeOffRequestTypeList />} />
        <Route path="time-off-request-types/create" element={<TimeOffRequestTypeForm />} />
        <Route path="time-off-request-types/edit/:id" element={<TimeOffRequestTypeForm />} /> 
        <Route path="time-off-request-types/view/:id" element={<TimeOffRequestTypeView />} />
        <Route path="time-off-requests" element={<TimeOffRequestsList />} />
        <Route path="time-off-requests/create" element={<TimeOffRequestsForm />} />
        <Route path="time-off-requests/edit/:id" element={<TimeOffRequestsForm />} />
        <Route path="time-off-requests/view/:id" element={<TimeOffRequestView />} />
        <Route path="payment-methods" element={<PaymentMethodList />} />    
        <Route path="payment-methods/create" element={<PaymentMethodForm />} />
        <Route path="payment-methods/edit/:id" element={<PaymentMethodForm />} />
        <Route path="payment-methods/view/:id" element={<PaymentMethodView />} />
        <Route path="payment-terms" element={<PaymentTermList />} />
        <Route path="payment-terms/create" element={<PaymentTermForm />} />
        <Route path="payment-terms/edit/:id" element={<PaymentTermForm />} />
        <Route path="payment-terms/view/:id" element={<PaymentTermView />} />
        <Route path="customer-tags" element={<CustomerTagList />} />
        <Route path="customer-tags/create" element={<CustomerTagForm />} />
        <Route path="customer-tags/edit/:id" element={<CustomerTagForm />} />
        <Route path="customer-tags/view/:id" element={<CustomerTagView />} />
        <Route path="estimate-job-tags" element={<EstimateJobTagList />} />
        <Route path="estimate-job-tags/create" element={<EstimateJobTagForm />} />
        <Route path="estimate-job-tags/edit/:id" element={<EstimateJobTagForm />} />
        <Route path="estimate-job-tags/view/:id" element={<EstimateJobTagView />} />
        <Route path="company-info" element={<CompanyInfo />} />
        <Route path="company-info/create" element={<CompanyInfoForm />} />
        <Route path="company-info/edit/:id" element={<CompanyInfoForm />} />
        <Route path="company-info/view/:id" element={<CompanyInfoView />} />
        <Route path="communication-templates" element={<CommunicationTemplates />} />
        <Route path="communication-templates/create" element={<CommunicationTemplateForm />} />
        <Route path="communication-templates/edit/:id" element={<CommunicationTemplateForm />} />
        <Route path="company-workforces" element={<CompanyWorkForce />} />
        <Route path="company-workforces/create" element={<CompanyWorkForceForm />} />
        <Route path="company-workforces/edit/:id" element={<CompanyWorkForceForm />} />
        <Route path="company-workforces/view/:id" element={<CompanyWorkForceView />} />
        <Route path="calendar" element={<MyCalendar />} /> 
        <Route path="tasks" element={<TaskList />} />
        <Route path="tasks/new" element={<TaskForm />} /> 
        <Route path="tasks/edit/:id" element={<TaskForm />} /> 
        <Route path="tasks/view/:id" element={<TaskView />} />
        <Route path="reports" element={<ReportDashboard />} />
        <Route path="roles" element={<RoleList />} />
        <Route path="roles/create" element={<RoleForm />} />
        <Route path="roles/edit/:id" element={<RoleForm />} />
        <Route path="estimation-statuses" element={<EstimationStatusList />} />
        <Route path="estimation-statuses/new" element={<EstimationStatusForm />} />
        <Route path="estimation-statuses/edit/:id" element={<EstimationStatusForm />} />
        <Route path="job-statuses" element={<JobStatusList />} />
        <Route path="job-statuses/new" element={<JobStatusForm />} />
        <Route path="job-statuses/edit/:id" element={<JobStatusForm />} />

      </Route>
    </Routes>
  );
};


// Add this new component
const CountryInitializer = () => {
  const { countries, getCountries, isApiReady } = useCountry();
  const initialized = React.useRef(false);

  useEffect(() => {
    if (isApiReady && !initialized.current && countries.length === 0) {
      initialized.current = true;
      getCountries();
    }
  }, [isApiReady, countries.length, getCountries]);

  return null;
};
   
function App() {
  
  const { setValue } = useForm();
 
  useEffect(() => {
    $(document).on('focus', '.datepicker', function () {
      if (!$(this).hasClass('hasDatepicker')) {
        $(this).datepicker({
          dateFormat: 'mm-dd-yy',
          onSelect: function (dateText) {
            // ✅ Set value to input
            this.value = dateText;

            // ✅ Create a real React-compatible input event
            const inputEvent = new Event('input', { bubbles: true });
            this.dispatchEvent(inputEvent);
          },
        });
      }
    });

    return () => {
      $(document).off('focus', '.datepicker');
    };
  }, [setValue]);

  useEffect(() => {
     
    const initializeScripts = async () => {
      try {
        // Wait for DOM to be fully loaded
        if (document.readyState === 'loading') {
          await new Promise(resolve => {
            document.addEventListener('DOMContentLoaded', resolve);
          });
        }
        
        // Add a small delay to ensure all React components are mounted
        await new Promise(resolve => setTimeout(resolve, 1000));
        
        // Load external scripts
        await loadAllScripts();
        
      } catch (error) {
        console.error('Error loading scripts:', error);
        // Don't throw the error, just log it
      }
    };

    initializeScripts();
  }, [ ]);

  return (
    <Router>
      <ErrorBoundary>
        <LoadingProvider>
          <AuthProvider>
            <ErrorProvider>
            <PermissionsProvider>
              <CountryProvider>
                <CountryInitializer />
                <PaymentTypeProvider>
                <StatusProvider>
                <StatusCategoriesProvider>
                <ProjectProvider>
                <ServiceCategoryProvider>
                <EquipmentManufacturerProvider>
                <EquipmentTypeProvider>
                <ChangePasswordProvider>
                  <ResetPasswordProvider>
                    <ForgotPasswordProvider>
                      <TaskProvider>
                        <CustomerProvider>
                          <JobProvider>
                            <CompanyWorkForceProvider>
                              <CompanyInfoProvider>
                                <CommunicationTemplatesProvider>
                                  <CommunicationTemplateCategoryProvider>
                                  <EstimateJobTagProvider>
                                    <CustomerTagProvider>
                                      <PaymentTermProvider>
                                        <PaymentMethodProvider>
                                          <TimeOffRequestTypeProvider>
                                          <TimeOffRequestsProvider>
                                          <EstimationTemplateProvider>
                                            <EstimationProvider>
                                              <ServiceProvider>
                                                <ProductCategoryProvider>
                                                  <ProductProvider>
                                                    <VendorProvider>
                                                      <InvoiceProvider>
                                                        <PaymentProvider>
                                                        <ExpenseCategoryProvider>
                                                        <JobCategoryProvider>
                                                        <RoleProvider>
                                                          <AppRoutes />
                                                          <ToastContainer />
                                                          <Toaster position="top-right" />
                                                        </RoleProvider>
                                                        </JobCategoryProvider>
                                                        </ExpenseCategoryProvider>
                                                        </PaymentProvider>
                                                      </InvoiceProvider>
                                                    </VendorProvider>
                                                  </ProductProvider>
                                                </ProductCategoryProvider>
                                              </ServiceProvider>
                                            </EstimationProvider>
                                            </EstimationTemplateProvider>
                                            </TimeOffRequestsProvider>
                                          </TimeOffRequestTypeProvider>
                                        </PaymentMethodProvider>
                                      </PaymentTermProvider>
                                    </CustomerTagProvider>
                                  </EstimateJobTagProvider>
                                  </CommunicationTemplateCategoryProvider>
                                </CommunicationTemplatesProvider>
                              </CompanyInfoProvider>
                            </CompanyWorkForceProvider>
                          </JobProvider>
                        </CustomerProvider>
                      </TaskProvider>
                    </ForgotPasswordProvider>
                  </ResetPasswordProvider>
                </ChangePasswordProvider>
                </EquipmentTypeProvider>
                </EquipmentManufacturerProvider>
                </ServiceCategoryProvider>
                </ProjectProvider>
                </StatusCategoriesProvider>
                </StatusProvider>
                </PaymentTypeProvider>
              </CountryProvider>
              </PermissionsProvider>
            </ErrorProvider>
          </AuthProvider>
        </LoadingProvider>
      </ErrorBoundary>
    </Router>
  );
}

export default App; 